import './FormsSection.css';

import React, {
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  get,
  post,
} from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  FaCheck,
  FaCloudDownloadAlt,
  FaEraser,
  FaFileDownload,
  FaFileSignature,
  FaHandshake,
  FaShieldAlt,
  FaSpinner,
} from 'react-icons/fa';
import SignatureCanvas from 'react-signature-canvas';

const FormsSection = ({ formProgress = {}, updateFormProgress, user }) => {
  // Default empty formProgress if not provided
  const safeFormProgress = formProgress || { contract: false, nda: false, loa: false };
  
  const [activeForm, setActiveForm] = useState('contract');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [signatureDataUrl, setSignatureDataUrl] = useState('');
  const [completedForms, setCompletedForms] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  
  // Refs for signature canvases
  const contractSignatureRef = useRef(null);
  const ndaSignatureRef = useRef(null);
  const loaSignatureRef = useRef(null);

  useEffect(() => {
    // Determine which form to show first based on progress
    if (!safeFormProgress.contract) {
      setActiveForm('contract');
    } else if (!safeFormProgress.nda) {
      setActiveForm('nda');
    } else if (!safeFormProgress.loa) {
      setActiveForm('loa');
    }
    
    // Fetch completed forms
    if (user) {
      fetchCompletedForms();
    }
  }, [safeFormProgress, user]);

  const getAuthToken = async () => {
    try {
      const session = await fetchAuthSession();
      return session.tokens.idToken.toString();
    } catch (error) {
      console.error('Error getting auth token:', error);
      throw error;
    }
  };

  const fetchCompletedForms = async () => {
    if (!user || !user.username) {
      console.log('No user available, skipping forms fetch');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      console.log('Fetching completed forms for user:', user.username);
      
      // Get auth token before making the request
      const token = await getAuthToken();
      
      const response = await get({
        apiName: 'UtilityAuditProAPI',
        path: `/users/${user.username}/forms`,
        options: {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      }).response;
      
      if (response.statusCode === 200) {
        const data = await response.body.json();
        console.log('Completed forms data:', data);
        
        if (data && data.forms) {
          setCompletedForms(data.forms);
        } else {
          console.log('No forms found or invalid response format');
          setCompletedForms({});
        }
        
        setFormError('');
      } else {
        throw new Error(`API returned status code ${response.statusCode}`);
      }
    } catch (error) {
      console.error('Error fetching completed forms:', error);
      setFormError('Failed to load your completed forms. Please refresh the page.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignatureChange = (signatureRef) => {
    if (signatureRef.current && !signatureRef.current.isEmpty()) {
      setSignatureDataUrl(signatureRef.current.toDataURL());
      console.log('Signature updated');
    } else {
      setSignatureDataUrl('');
    }
  };

  const clearSignature = (signatureRef) => {
    if (signatureRef.current) {
      signatureRef.current.clear();
      setSignatureDataUrl('');
    }
  };

  const handleSubmit = async (form) => {
    if (!user || !user.username) {
      setFormError('User authentication required. Please log in again.');
      return;
    }

    setIsSubmitting(true);
    setFormError('');
    
    try {
      // Get the signature data
      let signatureRef;
      switch(form) {
        case 'contract':
          signatureRef = contractSignatureRef;
          break;
        case 'nda':
          signatureRef = ndaSignatureRef;
          break;
        case 'loa':
          signatureRef = loaSignatureRef;
          break;
        default:
          throw new Error('Invalid form type');
      }
      
      if (signatureRef.current && signatureRef.current.isEmpty()) {
        setFormError('Please provide your signature before submitting.');
        setIsSubmitting(false);
        return;
      }

      const signatureData = signatureRef.current.toDataURL();
      
      // Log the signature data for debugging
      console.log('Signature data:', signatureData.substring(0, 50) + '...');
      
      // Get auth token before making the request
      const token = await getAuthToken();
      
      // Submit the form with signature
      const response = await post({
        apiName: 'UtilityAuditProAPI',
        path: '/submitForm',
        options: {
          headers: {
            Authorization: `Bearer ${token}`
          },
          body: {
            userId: user.username,
            formType: form,
            signatureData: signatureData,
            timestamp: new Date().getTime()
          }
        }
      }).response;
      
      if (response.statusCode === 200 || response.statusCode === 201) {
        console.log(`${form} form submitted successfully`);
        
        // Update form progress
        updateFormProgress(form, true);
        
        // Update completed forms
        setCompletedForms(prev => ({
          ...prev,
          [form]: {
            signatureUrl: `forms/${user.username}/${form}_${new Date().getTime()}.png`,
            submittedDate: new Date().toISOString()
          }
        }));
        
        // Move to next form
        if (form === 'contract') {
          setActiveForm('nda');
        } else if (form === 'nda') {
          setActiveForm('loa');
        }
        
        // Clear signature for next form
        setSignatureDataUrl('');
        setFormError('');
      } else {
        throw new Error(`API returned status code ${response.statusCode}`);
      }
    } catch (error) {
      console.error(`Error submitting ${form} form:`, error);
      setFormError(`Failed to submit ${form} form: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleDownloadForm = async (form) => {
    if (!user || !user.username) {
      setFormError('User authentication required. Please log in again.');
      return;
    }

    try {
      if (completedForms[form]) {
        console.log(`Downloading ${form} form for user:`, user.username);
        
        // Get auth token before making the request
        const token = await getAuthToken();
        
        // Get presigned URL for downloading
        const response = await get({
          apiName: 'UtilityAuditProAPI',
          path: `/forms/download/${form}/${user.username}`,
          options: {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        }).response;
        
        if (response.statusCode === 200) {
          const data = await response.body.json();
          
          if (data && data.downloadUrl) {
            // Open download in new tab
            window.open(data.downloadUrl, '_blank');
            setFormError('');
          } else {
            console.error('No download URL returned from API');
            setFormError(`Unable to download ${form} form. Please try again.`);
          }
        } else {
          throw new Error(`API returned status code ${response.statusCode}`);
        }
      }
    } catch (error) {
      console.error(`Error downloading ${form} form:`, error);
      setFormError(`Failed to download ${form} form: ${error.message}`);
    }
  };
  
  const navigateToUtilityBills = () => {
    // First, try to find the utility bills tab by data-section attribute
    const utilityBillsTab = document.querySelector('[data-section="utility-bills"]');
    if (utilityBillsTab) {
      // Trigger a click on the tab
      utilityBillsTab.click();
      return;
    }
    
    // Second, try to find by id
    const utilityBillsById = document.getElementById('utility-bills-tab');
    if (utilityBillsById) {
      utilityBillsById.click();
      return;
    }
    
    // Third, try to find by class or other selectors that might be unique to the tabs
    const allTabs = document.querySelectorAll('.dashboard-tab, .nav-tab, .tab-item');
    for (const tab of allTabs) {
      if (tab.textContent.includes('Utility Bills') || tab.getAttribute('aria-label')?.includes('Utility Bills')) {
        tab.click();
        return;
      }
    }
    
    // Last resort - update URL hash and dispatch event
    window.location.hash = '#utility-bills';
    window.dispatchEvent(new HashChangeEvent('hashchange'));
    
    // If all else fails - try to scroll to the utility bills section
    const utilityBillsSection = document.querySelector('.utility-bills-section');
    if (utilityBillsSection) {
      utilityBillsSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const renderContractForm = () => (
    <div className="form-card">
      <div className="form-header">
        <div className="form-icon">
          <FaHandshake />
        </div>
        <div className="form-title-section">
          <h3 className="form-title">Service Contract</h3>
          <p className="form-description">Please review and accept the service agreement to begin the utility audit process.</p>
        </div>
      </div>
      
      <div className="form-content">
        <div className="contract-text">
          <h4>Utility Audit Pro Service Agreement</h4>
          <p>This Service Agreement ("Agreement") is made between you ("Client") and Utility Audit Pro ("Provider").</p>
          
          <h5>1. Services</h5>
          <p>Provider will analyze Client's utility bills to identify potential savings, billing errors, and refund opportunities. Provider will communicate with utility companies on Client's behalf to resolve any identified issues.</p>
          
          <h5>2. Fee Structure</h5>
          <p>Provider charges a contingency fee equal to 50% of the identified savings and/or refunds. No fee will be charged if no savings or refunds are secured.</p>
          
          <h5>3. Term</h5>
          <p>This Agreement shall remain in effect for 12 months from the date of acceptance and shall automatically renew unless terminated by either party with 30 days' written notice.</p>
        </div>
        
        <div className="signature-section">
          <label>Signature:</label>
          <div className="signature-pad">
            <SignatureCanvas
              ref={contractSignatureRef}
              penColor='black'
              canvasProps={{
                className: 'signature-canvas',
                width: 500,
                height: 200,
              }}
              onEnd={() => handleSignatureChange(contractSignatureRef)}
            />
          </div>
          <div className="signature-actions">
            <button 
              type="button" 
              className="clear-signature-btn"
              onClick={() => clearSignature(contractSignatureRef)}
            >
              <FaEraser /> Clear
            </button>
          </div>
        </div>
        
        <div className="form-actions">
          <button
            className="submit-button"
            onClick={() => handleSubmit('contract')}
            disabled={isSubmitting || !signatureDataUrl}
          >
            {isSubmitting ? <><FaSpinner className="spinner" /> Submitting...</> : <>Accept & Sign <FaFileSignature /></>}
          </button>
        </div>
      </div>
    </div>
  );

  const renderNDAForm = () => (
    <div className="form-card">
      <div className="form-header">
        <div className="form-icon">
          <FaShieldAlt />
        </div>
        <div className="form-title-section">
          <h3 className="form-title">Non-Disclosure Agreement</h3>
          <p className="form-description">Please review and accept our confidentiality terms.</p>
        </div>
      </div>
      
      <div className="form-content">
        <div className="contract-text">
          <h4>Confidentiality Agreement</h4>
          <p>This Non-Disclosure Agreement ("NDA") is entered into between you ("Client") and Utility Audit Pro ("Provider").</p>
          
          <h5>1. Confidential Information</h5>
          <p>All information shared between parties including but not limited to utility bills, account numbers, billing history, and proprietary audit methods shall be considered confidential.</p>
          
          <h5>2. Obligations</h5>
          <p>Both parties agree to maintain the confidentiality of shared information and not disclose to third parties without prior written consent, except as required by law.</p>
          
          <h5>3. Term</h5>
          <p>This NDA shall remain in effect for 5 years from the date of acceptance or as long as the parties continue to exchange confidential information, whichever is longer.</p>
        </div>
        
        <div className="signature-section">
          <label>Signature:</label>
          <div className="signature-pad">
            <SignatureCanvas
              ref={ndaSignatureRef}
              penColor='black'
              canvasProps={{
                className: 'signature-canvas',
                width: 500,
                height: 200,
              }}
              onEnd={() => handleSignatureChange(ndaSignatureRef)}
            />
          </div>
          <div className="signature-actions">
            <button 
              type="button" 
              className="clear-signature-btn"
              onClick={() => clearSignature(ndaSignatureRef)}
            >
              <FaEraser /> Clear
            </button>
          </div>
        </div>
        
        <div className="form-checkboxes">
          <div className="form-checkbox">
            <input
              type="checkbox"
              id="agreesToConfidentiality"
              onChange={() => setSignatureDataUrl(signatureDataUrl ? '' : 'checked')}
              checked={!!signatureDataUrl}
            />
            <label htmlFor="agreesToConfidentiality">
              I agree to keep all information shared with Utility Audit Pro confidential
            </label>
          </div>
        </div>
        
        <div className="form-actions">
          <button
            className="submit-button"
            onClick={() => handleSubmit('nda')}
            disabled={isSubmitting || !signatureDataUrl}
          >
            {isSubmitting ? <><FaSpinner className="spinner" /> Submitting...</> : <>Accept Terms <FaCheck /></>}
          </button>
        </div>
      </div>
    </div>
  );

  const renderLOAForm = () => (
    <div className="form-card">
      <div className="form-header">
        <div className="form-icon">
          <FaFileSignature />
        </div>
        <div className="form-title-section">
          <h3 className="form-title">Letter of Authorization</h3>
          <p className="form-description">Please authorize Utility Audit Pro to act on your behalf with utility companies.</p>
        </div>
      </div>
      
      <div className="form-content">
        <div className="contract-text">
          <h4>Letter of Authorization</h4>
          <p>By completing this Letter of Authorization ("LOA"), you authorize Utility Audit Pro to act as your agent for the purpose of utility bill auditing.</p>
          
          <h5>1. Authorization Scope</h5>
          <p>You authorize Utility Audit Pro to request information from your utility providers, contest charges, negotiate refunds and adjustments, and communicate on your behalf regarding your utility accounts.</p>
          
          <h5>2. Limitation</h5>
          <p>This authorization does not grant Utility Audit Pro the right to switch service providers, cancel service, or make payments on your behalf without explicit additional permission.</p>
          
          <h5>3. Term</h5>
          <p>This LOA shall remain in effect for 12 months from the date of acceptance or until utility audit services are completed, whichever is later.</p>
        </div>
        
        <div className="signature-section">
          <label>Signature:</label>
          <div className="signature-pad">
            <SignatureCanvas
              ref={loaSignatureRef}
              penColor='black'
              canvasProps={{
                className: 'signature-canvas',
                width: 500,
                height: 200,
              }}
              onEnd={() => handleSignatureChange(loaSignatureRef)}
            />
          </div>
          <div className="signature-actions">
            <button 
              type="button" 
              className="clear-signature-btn"
              onClick={() => clearSignature(loaSignatureRef)}
            >
              <FaEraser /> Clear
            </button>
          </div>
        </div>
        
        <div className="form-checkboxes">
          <div className="form-checkbox">
            <input
              type="checkbox"
              id="authorizesToBill"
              onChange={() => setSignatureDataUrl(signatureDataUrl ? '' : 'checked')}
              checked={!!signatureDataUrl}
            />
            <label htmlFor="authorizesToBill">
              I authorize Utility Audit Pro to request information from my utility providers and negotiate on my behalf
            </label>
          </div>
        </div>
        
        <div className="form-actions">
          <button
            className="submit-button"
            onClick={() => handleSubmit('loa')}
            disabled={isSubmitting || !signatureDataUrl}
          >
            {isSubmitting ? <><FaSpinner className="spinner" /> Submitting...</> : <>Authorize <FaCheck /></>}
          </button>
        </div>
      </div>
    </div>
  );

  const renderCompletedFormsTabs = () => (
    <div className="completed-forms-section">
      <h3>Completed Forms</h3>
      <div className="completed-forms-list">
        {safeFormProgress.contract && (
          <div className="completed-form-item">
            <div className="completed-form-details">
              <div className="form-type">Service Contract</div>
              <div className="submitted-date">
                {completedForms.contract ? new Date(completedForms.contract.submittedDate).toLocaleDateString() : 'Submitted'}
              </div>
            </div>
            <button 
              className="download-form-btn" 
              onClick={() => handleDownloadForm('contract')}
              title="Download Service Contract"
            >
              <FaFileDownload /> Download
            </button>
          </div>
        )}
        
        {safeFormProgress.nda && (
          <div className="completed-form-item">
            <div className="completed-form-details">
              <div className="form-type">Non-Disclosure Agreement</div>
              <div className="submitted-date">
                {completedForms.nda ? new Date(completedForms.nda.submittedDate).toLocaleDateString() : 'Submitted'}
              </div>
            </div>
            <button 
              className="download-form-btn" 
              onClick={() => handleDownloadForm('nda')}
              title="Download NDA"
            >
              <FaFileDownload /> Download
            </button>
          </div>
        )}
        
        {safeFormProgress.loa && (
          <div className="completed-form-item">
            <div className="completed-form-details">
              <div className="form-type">Letter of Authorization</div>
              <div className="submitted-date">
                {completedForms.loa ? new Date(completedForms.loa.submittedDate).toLocaleDateString() : 'Submitted'}
              </div>
            </div>
            <button 
              className="download-form-btn" 
              onClick={() => handleDownloadForm('loa')}
              title="Download LOA"
            >
              <FaFileDownload /> Download
            </button>
          </div>
        )}
        
        {!safeFormProgress.contract && !safeFormProgress.nda && !safeFormProgress.loa && (
          <div className="no-completed-forms">
            <p>No completed forms yet. Please sign the required forms to proceed.</p>
          </div>
        )}
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="forms-section">
        <div className="section-header">
          <h2 className="section-title">Required Forms</h2>
          <p className="section-description">Loading forms...</p>
        </div>
        <div className="loading-wrapper">
          <FaSpinner className="spinner" />
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="forms-section">
        <div className="section-header">
          <h2 className="section-title">Required Forms</h2>
          <p className="section-description">Loading user information...</p>
        </div>
        <div className="loading-wrapper">
          <FaSpinner className="spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="forms-section">
      <div className="section-header">
        <h2 className="section-title">Required Forms</h2>
        <p className="section-description">Please complete the following forms to begin the utility bill analysis process.</p>
      </div>
      
      {formError && <div className="error-message">{formError}</div>}
      
      <div className="forms-flow-container">
        <div className={`form-flow-box ${activeForm === 'contract' ? 'active' : ''} ${safeFormProgress.contract ? 'completed' : ''}`}>
          <div className="form-box-icon">
            {safeFormProgress.contract ? <FaCheck /> : <FaHandshake />}
          </div>
          <div className="form-box-content">
            <h4>Service Contract</h4>
            <div className="form-box-status">
              {safeFormProgress.contract ? 'Completed' : activeForm === 'contract' ? 'In Progress' : 'Waiting'}
            </div>
          </div>
          {safeFormProgress.contract && (
            <button
              className="form-box-download"
              onClick={() => handleDownloadForm('contract')}
              title="Download Service Contract"
            >
              <FaCloudDownloadAlt />
            </button>
          )}
        </div>

        <div className="form-flow-arrow">→</div>
        
        <div className={`form-flow-box ${activeForm === 'nda' ? 'active' : ''} ${safeFormProgress.nda ? 'completed' : ''}`}>
          <div className="form-box-icon">
            {safeFormProgress.nda ? <FaCheck /> : <FaShieldAlt />}
          </div>
          <div className="form-box-content">
            <h4>Non-Disclosure</h4>
            <div className="form-box-status">
              {safeFormProgress.nda ? 'Completed' : activeForm === 'nda' ? 'In Progress' : 'Waiting'}
            </div>
          </div>
          {safeFormProgress.nda && (
            <button
              className="form-box-download"
              onClick={() => handleDownloadForm('nda')}
              title="Download NDA"
            >
              <FaCloudDownloadAlt />
            </button>
          )}
        </div>

        <div className="form-flow-arrow">→</div>
        
        <div className={`form-flow-box ${activeForm === 'loa' ? 'active' : ''} ${safeFormProgress.loa ? 'completed' : ''}`}>
          <div className="form-box-icon">
            {safeFormProgress.loa ? <FaCheck /> : <FaFileSignature />}
          </div>
          <div className="form-box-content">
            <h4>Authorization</h4>
            <div className="form-box-status">
              {safeFormProgress.loa ? 'Completed' : activeForm === 'loa' ? 'In Progress' : 'Waiting'}
            </div>
          </div>
          {safeFormProgress.loa && (
            <button
              className="form-box-download"
              onClick={() => handleDownloadForm('loa')}
              title="Download LOA"
            >
              <FaCloudDownloadAlt />
            </button>
          )}
        </div>
      </div>
      
      <div className="form-content-wrapper">
        {activeForm === 'contract' && !safeFormProgress.contract && renderContractForm()}
        {activeForm === 'nda' && !safeFormProgress.nda && renderNDAForm()}
        {activeForm === 'loa' && !safeFormProgress.loa && renderLOAForm()}
        
        {safeFormProgress.contract && safeFormProgress.nda && safeFormProgress.loa && (
          <div className="all-forms-completed">
            <div className="completion-icon">
              <FaCheck />
            </div>
            <h3>All forms completed!</h3>
            <p>You can now proceed to upload your utility bills.</p>
            <button 
              className="action-button"
              onClick={navigateToUtilityBills}
            >
              Continue to Utility Bills
            </button>
          </div>
        )}
        
        {(safeFormProgress.contract || safeFormProgress.nda || safeFormProgress.loa) && renderCompletedFormsTabs()}
      </div>
    </div>
  );
};

export default FormsSection;